import React, { useCallback, useRef, useState } from 'react';
import { Form, Button, Input, Card, Result, Divider, Modal, App } from "antd";
import { apis, hooks, models } from "@vote4dance/judging-sdk";
var Api = apis.Api;
import { _T } from "../src/Localization";
var useSession = hooks.sessions.useSession;
var Session = apis.sessions.Session;
var UserSource = models.users.UserSource;
import { Link, useLocation } from 'react-router-dom';
import { Localization } from '../src/Localization';
import { getSafeRedirectUrl } from "../src/safeRedirect";
import { getBackendResponse, isCaptchaFailure } from "../src/errorResponse";
import { useCaptchaKey } from '../hooks/useCaptchaKey';
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { Loading } from './Loading';
const LoginFormInner = ({ reload = true, compact = false, getCaptchaToken }) => {
    var _a;
    const [loading, setLoading] = useState(false);
    const { notification: notificationApi } = App.useApp();
    const sessionState = useSession();
    const location = useLocation();
    const [resetPasswordVisible, setResetPasswordVisible] = useState(false);
    const inputRef = useRef(null);
    const onFinish = async (values) => {
        var _a;
        setLoading(true);
        const token = await getCaptchaToken('login');
        const skipCaptcha = token == null;
        try {
            const sessionInfo = await Api.call("/session", "login", {
                user_email: values.email,
                user_pass: values.password,
                recaptcha_token: token
            });
            Session.get().handleSessionInfo(sessionInfo);
            const userData = await Api.read(UserSource(sessionInfo.user_id).url);
            if (userData[0].user_language && userData[0].user_language != Localization.getLanguage()) {
                Localization.changeLanguage(userData[0].user_language);
            }
            const redirectTarget = getSafeRedirectUrl((_a = location.state) === null || _a === void 0 ? void 0 : _a.from);
            if (redirectTarget) {
                window.location.assign(redirectTarget);
            }
            else if (reload) {
                window.location.assign(window.location.origin);
            }
        }
        catch (err) {
            setLoading(false);
            const response = getBackendResponse(err);
            const message = isCaptchaFailure(response)
                ? (!skipCaptcha && _T("common.captcha"))
                : _T("login.failed");
            notificationApi.error({ title: message });
        }
    };
    const onResetPassword = async () => {
        var _a, _b;
        const resetEmail = (_b = (_a = inputRef === null || inputRef === void 0 ? void 0 : inputRef.current) === null || _a === void 0 ? void 0 : _a.input) === null || _b === void 0 ? void 0 : _b.value;
        const origin = window.location.protocol + '//' + window.location.hostname + (window.location.port ? ':' + window.location.port : '');
        if (!resetEmail) {
            return;
        }
        setLoading(true);
        const token = await getCaptchaToken('forgot-password');
        const skipCaptcha = token == null;
        try {
            await Api.call("/session", "forgot-password", {
                user_email: resetEmail,
                origin,
                recaptcha_token: token
            });
            notificationApi.success({
                title: _T('login.reset.sent')
            });
        }
        catch (err) {
            const response = getBackendResponse(err);
            const message = isCaptchaFailure(response)
                ? (!skipCaptcha && _T("common.captcha"))
                : _T("login.failed");
            notificationApi.error({ title: message });
        }
        finally {
            setResetPasswordVisible(false);
            setLoading(false);
        }
    };
    return (React.createElement("div", { style: {
            width: "100%",
            height: compact ? 'inherit' : "90vh",
            display: "flex",
            justifyContent: "center",
            alignItems: "center"
        } }, (sessionState === null || sessionState === void 0 ? void 0 : sessionState.isLoggedIn) ?
        React.createElement(Result, { status: "success", title: _T('login.success'), extra: !((_a = location.state) === null || _a === void 0 ? void 0 : _a.from) && React.createElement(Link, { to: window.location.origin },
                React.createElement(Button, { type: "primary" }, _T('login.home'))) })
        :
            React.createElement(Card, { title: _T('login.title') },
                React.createElement(Form, { name: "LoginForm", labelCol: { span: 8 }, wrapperCol: { span: 16 }, onFinish: onFinish, autoComplete: "off" },
                    React.createElement(Form.Item, { label: _T('login.email'), name: "email", rules: [{ required: true, message: _T('login.emailInputMessage') }] },
                        React.createElement(Input, { "data-testid": "email", autoFocus: true, autoComplete: 'email' })),
                    React.createElement(Form.Item, { label: _T('login.password'), name: "password", rules: [{ required: true, message: _T('login.passwordInputMessage') }], help: React.createElement(Button, { type: "link", onClick: () => setResetPasswordVisible(true) }, _T('login.reset.forgot')) },
                        React.createElement(Input.Password, { "data-testid": "password", autoComplete: 'current-password' })),
                    React.createElement(Form.Item, { wrapperCol: { span: 24 }, style: { textAlign: 'center' } },
                        React.createElement(Button, { type: "primary", htmlType: "submit", loading: loading, "data-testid": "submit" }, _T('login.submit')))),
                React.createElement(Divider, null),
                React.createElement("div", { style: { display: 'flex', flexWrap: 'wrap', gap: '0.5rem', justifyContent: 'center' } },
                    React.createElement("span", null, _T('login.new')),
                    React.createElement(Link, { to: '/register', state: location.state, style: { whiteSpace: 'nowrap' } }, _T('register.title'))),
                React.createElement(Modal, { title: _T('login.reset.title'), open: resetPasswordVisible, destroyOnHidden: true, confirmLoading: loading, okText: _T('common.ok'), cancelText: _T('common.cancel.title'), onCancel: () => setResetPasswordVisible(false), onOk: onResetPassword, afterOpenChange: open => { var _a; return open && ((_a = inputRef === null || inputRef === void 0 ? void 0 : inputRef.current) === null || _a === void 0 ? void 0 : _a.focus()); } },
                    React.createElement(Input, { "data-testid": "email", autoComplete: 'email', ref: inputRef, onPressEnter: onResetPassword, placeholder: _T('login.email') })))));
};
const LoginFormWithCaptcha = (props) => {
    const { executeRecaptcha } = useGoogleReCaptcha();
    const getCaptchaToken = useCallback(async (action) => {
        if (!executeRecaptcha) {
            return undefined;
        }
        try {
            return await executeRecaptcha(action);
        }
        catch (err) {
            console.error('Captcha execution failed', err);
            return undefined;
        }
    }, [executeRecaptcha]);
    return React.createElement(LoginFormInner, { ...props, getCaptchaToken: getCaptchaToken });
};
export const LoginForm = ({ reload = true, compact = false }) => {
    const captchaKey = useCaptchaKey();
    if (captchaKey === undefined) {
        return React.createElement(Loading, null);
    }
    const fallback = (React.createElement(LoginFormInner, { reload: reload, compact: compact, getCaptchaToken: async () => undefined }));
    if (captchaKey === null) {
        return fallback;
    }
    return (React.createElement(GoogleReCaptchaProvider, { reCaptchaKey: captchaKey },
        React.createElement(LoginFormWithCaptcha, { reload: reload, compact: compact })));
};
